import React, { useEffect } from 'react'
import { useAudioRecorder } from '../hooks/useAudioRecorder'
import { useAudioPreview } from '../hooks/useAudioPreview'
import { Icon } from '@codeleap/web'
import { Waveform } from './Waveform'
import { createStyles } from '@codeleap/styles'
import { theme } from '@/config'

interface AudioRecorderProps {
  onComplete: (audioBlob: Blob, duration: number) => void
  onCancel: () => void
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export const AudioRecorder: React.FC<AudioRecorderProps> = ({ onComplete, onCancel }) => {
  const {
    recordingState,
    recordingTime,
    audioUrl,
    waveformSamples,
    startRecording,
    stopRecording,
    sendRecording,
    cancelRecording,
    error,
  } = useAudioRecorder()

  const preview = useAudioPreview(audioUrl)

  // Auto-start on mount; cancel on unmount so the mic is released even if
  // the component unmounts mid-recording (Strict Mode double-invokes effects
  // in dev — without this the first stream would leak).
  useEffect(() => {
    startRecording()
    return () => {
      cancelRecording()
    }
  }, [startRecording, cancelRecording])

  const handleCancel = () => {
    cancelRecording()
    onCancel()
  }

  const handleSend = () => {
    sendRecording(onComplete)
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorText}>Error: {error}</p>
        <button onClick={handleCancel} style={styles.errorButton}>Close</button>
      </div>
    )
  }

  if (recordingState === 'idle') {
    return null
  }

  const isRecording = recordingState === 'recording'
  const isPreview = recordingState === 'stopped'

  return (
    <div style={styles.wrapper}>
      <button onClick={handleCancel} style={styles.iconButton} aria-label="Discard recording">
        <Icon name="trash-03" size={24} style={styles.trashIcon} />
      </button>

      {isRecording && (
        <>
          <div style={{ ...styles.recordingDot, animation: 'pulse 1.5s infinite' }} />
          <span style={styles.timer}>{formatTime(recordingTime)}</span>
          <Waveform isRecording={true} />
          <button onClick={stopRecording} style={styles.iconButton} aria-label="Stop recording">
            <Icon name="pause-circle" size={24} style={styles.controlIcon} />
          </button>
        </>
      )}

      {isPreview && (
        <>
          <button onClick={preview.toggle} style={styles.iconButton} aria-label={preview.isPlaying ? 'Pause preview' : 'Play preview'}>
            <Icon
              name={preview.isPlaying ? 'pause-01' : 'play-01'}
              size={24}
              style={styles.controlIcon}
            />
          </button>
          <Waveform
            isRecording={false}
            playbackProgress={preview.playbackProgress}
            samples={waveformSamples}
          />
          <span style={styles.timer}>{formatTime(recordingTime)}</span>
        </>
      )}

      <button onClick={handleSend} style={styles.sendButton} aria-label="Send recording">
        <Icon name="arrow-up" size={24} style={styles.sendIcon} />
      </button>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  )
}

const styles = createStyles(theme => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    ...theme.spacing.gap(1),
  },
  iconButton: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    ...theme.spacing.padding(0),
  },
  trashIcon: {
    color: '#EF4444',
  },
  controlIcon: {
    color: theme.baseColors?.primarySolid500 || '#4F46E5',
  },
  recordingDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#EF4444',
  },
  timer: {
    fontFamily: 'Inter',
    fontSize: 18,
    fontWeight: 400,
    lineHeight: 1.2,
    color: theme.colors.textHeadingHighlighted || '#000000',
  },
  sendButton: {
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.baseColors?.primarySolid500 || '#4F46E5',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    ...theme.spacing.padding(0),
  },
  sendIcon: {
    color: '#FFFFFF',
  },
  errorContainer: {
    ...theme.spacing.padding(2),
    background: '#fee',
    borderRadius: 8,
    ...theme.spacing.marginBottom(1),
  },
  errorText: {
    color: '#c00',
    ...theme.spacing.margin(0),
  },
  errorButton: {
    ...theme.spacing.marginTop(1),
  },
}))
